import z from 'zod';

import { IVariant } from '@/lib/schemas/Variant.zod';
import { Address, Cart, Category, Collection, Color, Customer, Line, Order, Picture, Post, Product, Sale, Setting, ShippingProvider, Size, Variant } from '@/model/schemas';

/**
 * Zod helpers for coercion.
 *
 * We have places where IDs / numeric fields are stored as strings in sessionStorage
 * or passed from UI layers. This helper turns those into numbers when possible,
 * but treats invalid values (e.g. NaN, empty string) as undefined.
 */
const coerceInt = () =>
  z.preprocess((value) => {
    if (value === null || value === undefined || value === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : Math.trunc(num);
  }, z.number().int());

/**
 * Checkout payload schemas (lightweight subset of full backend models).
 */
export const CheckoutCustomerSchema = z.object({
  id: coerceInt().optional().nullable(),
  email: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  enterprise: z.string().optional().nullable(),
  tax_id: z.string().optional().nullable(),
});

export const CheckoutPayMethodSchema = z.object({
  id: coerceInt().optional().nullable(),
  code: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
});

export const CheckoutShippingMethodSchema = z.object({
  id: coerceInt().optional().nullable(),
  name: z.string().optional().nullable(),
});

export const CheckoutAddressSchema = z.object({
  id: z.union([z.string(), z.number().transform((n) => String(n))]).optional().nullable(),
  location: z.string().optional().nullable(),
});

export const CheckoutOrderSchema = z.object({
  id: coerceInt().optional().nullable(),
  quantity: coerceInt(),
  product: z.object({
    id: coerceInt().optional().nullable(),
    name: z.string().optional().nullable(),
  }),
  variant: z.object({ id: coerceInt().optional().nullable() }).optional().nullable(),
});

export const CheckoutDataSchema = z.object({
  pay_method: CheckoutPayMethodSchema,
  customer: CheckoutCustomerSchema.optional().nullable(),
  coupons: z.array(z.any()).optional().nullable(),
  sales: z.array(z.any()).optional().nullable(),
  shipping: z.object({
    method: CheckoutShippingMethodSchema.optional().nullable(),
    destination: CheckoutAddressSchema.optional().nullable(),
  }),
  shipping_provider_id: coerceInt().optional().nullable(),
  orders: z.array(CheckoutOrderSchema),
  currency: z.string().optional().nullable(),
  options: z.any().optional().nullable(),
});

export type CheckoutData = z.infer<typeof CheckoutDataSchema>;
export type CheckoutOrder = z.infer<typeof CheckoutOrderSchema>;

export type ICart = Cart;
export type IOrder = Order & {
  product: Product & {
    name: string;
    variant?: Variant & { id?: number; name: string };
    discounted_price?: number;
    sale?: Sale & {
      name: string;
      discount_value: number;
      discount_type: string;
      valid_from: string;
      valid_to: string;
    };
  };
  variant?: Variant & { id?: number; name: string };
  price_type?: 'PUBLIC' | 'RETAIL' | 'WHOLESALE';
  /**
   * Local-only helper: tracks applied discounts for cart items (used in the UI).
   */
  cart_discounts?: { id: string }[];
};

export type ICartEnriched = ICart & {
  orders?: IOrder[];
  pay_method?: { id?: number; code?: string; type?: string; name?: string };
  destination_address?: Record<string, unknown>;
};

// ─── Response Types ───────────────────────────────────────────────────────────

export type IMercadoPagoCheckoutResponse = {
  id?: string;
  status?: string;
  preference?: {
    id?: string;
    init_point?: string;
    sandbox_init_point?: string;
    [key: string]: any;
  };
  sandbox_init_point?: string;
  init_point?: string;
  [key: string]: any;
};

export type IBankTransferCheckoutResponse = {
  id?: string;
  status?: string;
  instructions?: string | null;
  [key: string]: any;
};

export type IProduct = Product & {
  picture?: Picture & { url: string };
  line?: Line & { name: string };
  collection?: Collection & { name: string };
  category?: Category & { name: string };
  gallery?: Picture & { url: string }[];
  max_installments?: number;
  sizes?: Size & { name: string; order?: number }[];
  colors: Color & { name: string; hex?: string }[];
  variants: Variant &
    {
      id: number;
      color: Color & { name: string; hex?: string };
      size: Size & { name: string; order?: number };
    }[]; // Include variants for selector
  sale?: Sale & {
    name: string;
    discount_value: number;
    discount_type: string;
    valid_from: string;
    valid_to: string;
  };
  /**
   * If the backend calculates a net (discounted) price, this field can be used
   * by the UI to display the true price and compute discounts.
   */
  discounted_price?: number;
};

// ─── Auxiliary Types ─────────────────────────────────────────────────────────
export type ILine = Line & { picture?: Picture & { url: string }; gallery?: Picture & { url: string }[] };
export type ICategory = Category & { name: string };
export type ISize = Size & { name: string; order?: number };
export type IColor = Color & { name: string; hex?: string };

export type FilterData = {
  categories: ICategory[];
  lines: ILine[];
  sizes: ISize[];
};

export type ISetting = Setting;

export type IPost = Post & { content: string; excerpt: string; picture?: Picture & { url: string } };

export type IAddress = Address & {};
export type IShippingLocation = IAddress & {
  name?: string;
};
export type IShippingProvider = ShippingProvider & {};
export type IPaymentMethod = z.infer<typeof CheckoutPayMethodSchema> & {};

export type IAccountProfile = Omit<Customer, 'birthdate'> & {
  id?: string;
  email?: string;
  enterprise?: string;
  tax_id?: string;
  id_number?: string;
  id_type?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  // Accept either a raw ISO string (from forms/localstorage) or a Date (from the backend)
  birthdate?: string | Date | null;
  picture?: Picture & { url: string };
  [key: string]: any;
};


